import { useNavigate } from "react-router-dom";
import { ChevronRight, Check, Feather as FeatherIcon, PenTool } from "lucide-react";
import {
  activePledges,
  personById,
  pledgedForReq,
  projectProgress,
  totalPeopleCommitted,
  totalPeopleNeeded,
  openRequirements,
  formatCount,
  labelForCount,
  uniqueHelpersCount,
  attendanceCount,
  personIsIn,
  effectiveStatus,
} from "../lib/domain";
import { statusColor, statusLabelDynamic, tintForStatus } from "../lib/theme";
import {
  isCruiseShipProject,
  cruiseTravelerHeadline,
  cruiseSupportingGaps,
} from "../lib/cruiseDemo";

function launchesLineFor(projectId, needed) {
  switch (projectId) {
    case "clean-maple-creek": return `Launches when ${needed} of us commit.`;
    case "community-garden": return `Everyone gets notified when ${needed} commit.`;
    case "repair-cafe": return `Launches at ${needed}.`;
    case "save-bus-stop": return `Gathers once ${needed} of us commit.`;
    case "coats-attempt-3": return `Drive begins when ${needed} people commit.`;
    default: return `Launches at ${needed}.`;
  }
}

function humanize(label) {
  if (!label) return label;
  const w = label[0].toUpperCase() + label.slice(1);
  if (w === "Musician") return "Music is";
  if (w === "Volunteer") return "Volunteers are";
  if (w === "Grill volunteer") return "Grill team is";
  if (w === "Driver") return "Drivers are";
  if (w === "Photographer") return "Photography is";
  return `${w} is`;
}

function describeGap(req, gap) {
  if (req.kind === "people") return formatCount(gap, req.label, req.pluralLabel).toLowerCase();
  return `${gap} ${labelForCount(gap, req.label, req.pluralLabel).toLowerCase()}`;
}

function momentumLineFor(project, pledges) {
  const open = openRequirements(project, pledges);
  if (!open.length) return "Everything is here — any moment now.";
  const gaps = open
    .map((r) => ({ req: r, gap: Math.max(0, r.needed - pledgedForReq(r.id, pledges)) }))
    .filter((x) => x.gap > 0)
    .sort((a, b) => b.gap - a.gap);
  if (!gaps.length) return "Everything is here — any moment now.";
  const total = gaps.reduce((s, x) => s + x.gap, 0);
  const [first, second] = gaps;
  if (total === 1) {
    const r = first.req;
    if (r.kind === "people") return `Waiting on one final ${r.label.toLowerCase()}.`;
    return `Just one ${r.label.toLowerCase()} left.`;
  }
  if (gaps.length >= 2) {
    const covered = project.requirements.find(
      (r) => pledgedForReq(r.id, pledges) >= r.needed && r.kind === "people"
    );
    const firstT = describeGap(first.req, first.gap);
    const secondT = describeGap(second.req, second.gap);
    if (covered) return `${humanize(covered.label)} covered. Still looking for ${firstT} and ${secondT}.`;
    return `Looking for ${firstT} and ${secondT}.`;
  }
  const r = first.req;
  if (r.kind === "people") return `Looking for ${formatCount(first.gap, r.label, r.pluralLabel).toLowerCase()}.`;
  return `${first.gap} more ${labelForCount(first.gap, r.label, r.pluralLabel).toLowerCase()} to go.`;
}

function buildPreview(project, pledges, people) {
  if (!people?.length) return { chips: [], overflow: 0 };
  const active = activePledges(project.id, pledges);
  const seen = new Set();
  const ordered = [];
  [...active].sort((a, b) => a.timestamp - b.timestamp).forEach((p) => {
    if (seen.has(p.personId)) return;
    seen.add(p.personId);
    ordered.push(p.personId);
  });
  const shown = ordered.slice(0, 3);
  const chips = shown.map((id) => {
    const person = personById(people, id);
    const name = person?.name || "?";
    return { initial: name.trim().charAt(0).toUpperCase() || "?", label: name };
  });
  return { chips, overflow: Math.max(0, ordered.length - shown.length) };
}

export default function ProjectCard({ project, pledges, people, provenance, youAreIn, onClick }) {
  const nav = useNavigate();
  const status = effectiveStatus(project, pledges);
  const accent = statusColor(status);
  const helpers = uniqueHelpersCount(project.id, pledges);
  const showedUp = attendanceCount(project, pledges);
  const peopleCommitted = totalPeopleCommitted(project, pledges);
  const peopleNeeded = totalPeopleNeeded(project);
  const isDraft = status === "draft";
  const isCompleted = status === "completed";
  const isClosed = status === "closed-not-met";
  const isCancelled = status === "cancelled";
  const isHappening = status === "threshold-crossed" || status === "happening";
  const isGathering = status === "gathering";
  const preview = buildPreview(project, pledges, people);
  const momentum = isGathering ? momentumLineFor(project, pledges) : null;
  const showRoots = !isDraft && helpers > 0;
  // TODO(cruise-demo): Remove once requirement-level threshold
  // summaries have a general design. All Cruise Ship-specific values
  // are read from the project itself (via `lib/cruiseDemo.js`) so the
  // preview card cannot drift from the Project Detail override.
  const cruise = isCruiseShipProject(project);
  const cruiseHead = cruise ? cruiseTravelerHeadline(project, pledges) : null;
  const cruiseStillNeed = cruise && isGathering ? cruiseSupportingGaps(project, pledges) : null;
  const rootsChip = showRoots ? (
    <span
      className="c-roots-chip"
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); nav(`/roots/${project.id}`); }}
      data-testid={`roots-chip-${project.id}`}
    >
      <span>🌱</span>
      <span className="c-roots-chip-text">Roots</span>
      {preview.chips.length ? (
        <span className="c-roots-avatars">
          {preview.chips.map((c, i) => (
            <span key={i} className="c-avatar">{c.initial}</span>
          ))}
          {preview.overflow > 0 ? (
            <span className="c-roots-plus">+{preview.overflow}</span>
          ) : null}
        </span>
      ) : null}
      <ChevronRight size={14} color="var(--c-on-surface-2)" />
    </span>
  ) : null;

  const secondary = (() => {
    if (isDraft) return "Draft · not yet public";
    if (isCancelled) return "Cancelled by the steward";
    if (isClosed) return `Didn't gather in time · ${peopleCommitted} of ${peopleNeeded} committed`;
    if (isCompleted) return `${project.when} · ${showedUp} showed up`;
    if (isHappening) return `It's happening · ${project.when}`;
    // TODO(cruise-demo): Cruise Ship uses the Traveler headline
    // instead of the generic aggregate for its momentum copy.
    if (cruise && cruiseHead) {
      const filled = cruiseHead.filled;
      if (filled === 1) return "Early momentum · one person is down so far.";
      return `Early momentum · ${filled} people are down so far.`;
    }
    const remaining = Math.max(0, peopleNeeded - peopleCommitted);
    if (remaining === 0) return "All the people are in. Waiting on a few things.";
    // Emotional arc across gathering: early momentum → building → almost.
    // Under ~40% we celebrate the arrival, not the shortfall, so the card
    // reads "something is starting" rather than "something is missing".
    const progress = peopleNeeded > 0 ? peopleCommitted / peopleNeeded : 0;
    if (progress < 0.4) {
      if (peopleCommitted === 1) return "Early momentum · one person has committed so far.";
      return `Early momentum · ${peopleCommitted} people have committed so far.`;
    }
    if (remaining === 1) return "One more person to make it happen";
    return `${remaining} more people to make it happen`;
  })();

  return (
    <button
      className="c-card"
      style={{
        background: tintForStatus(status),
        opacity: isCompleted ? 0.88 : (isClosed || isCancelled ? 0.92 : 1),
      }}
      onClick={onClick || (() => nav(`/project/${project.id}`))}
      data-testid={`project-card-${project.id}`}
    >
      <div className="c-card-accent" style={{ background: accent }} />
      <div className="c-card-body">
        <div className="c-card-head">
          <div className="c-card-emoji">{project.emoji}</div>
          <div className="c-card-title-wrap">
            <div className="c-card-title" data-testid={`project-title-${project.id}`}>
              {project.title}
              {project.attemptNumber && project.attemptNumber > 1 ? (
                <span className="c-card-attempt">  · Attempt {project.attemptNumber}</span>
              ) : null}
            </div>
            <div className="c-card-secondary" style={{ color: accent }}>{secondary}</div>
          </div>
          <div
            className={`c-status-badge ${isHappening ? "c-status-badge-happening" : ""} ${isCompleted ? "c-status-badge-completed" : ""}`}
            style={(isHappening || isCompleted) ? undefined : { borderColor: accent, color: accent }}
          >
            {statusLabelDynamic(status, peopleCommitted, peopleNeeded, !!project.previousAttemptId)}
          </div>
        </div>

        <div className="c-card-purpose">{project.purpose}</div>

        {project.commitBy && (isGathering || status === "threshold-crossed") ? (
          <div className="c-dates-row">
            <div className="c-dates-text">📅 Commit by: <strong>{project.commitBy}</strong></div>
            <div className="c-dates-text">🎉 Happens: <strong>{project.when}</strong></div>
          </div>
        ) : null}

        {provenance ? (
          <div
            className="c-provenance"
            data-testid={`provenance-${provenance.kind}-${project.id}`}
          >
            {provenance.kind === "created" ? (
              <><FeatherIcon size={12} color="var(--c-brand-primary)" /> You started this</>
            ) : provenance.kind === "stewarding" ? (
              <><PenTool size={12} color="var(--c-brand-primary)" /> You're stewarding this</>
            ) : (
              <>🌱 Invited by <strong>{provenance.inviterName}</strong></>
            )}
          </div>
        ) : null}

        {isGathering ? (
          <div className="c-threshold">
            <div className="c-threshold-head">
              <div>
                {/* TODO(cruise-demo): Cruise Ship uses the Traveler
                    requirement as the defining headline; every other
                    project keeps the shared aggregate. */}
                {cruise && cruiseHead ? (
                  <>
                    <span className="c-threshold-big" style={{ color: accent }}>
                      {cruiseHead.filled}
                    </span>
                    <span className="c-threshold-of">
                      {" "}of {cruiseHead.needed.toLocaleString("en-US")} people are down
                    </span>
                  </>
                ) : (
                  <>
                    <span className="c-threshold-big" style={{ color: accent }}>{peopleCommitted}</span>
                    <span className="c-threshold-of"> of {peopleNeeded} committed</span>
                  </>
                )}
              </div>
              {youAreIn ? (
                <div className="c-you-chip"><Check size={12} /> You're in</div>
              ) : null}
            </div>
            {/* TODO(cruise-demo): Suppress the false "Launches at N"
                singular threshold for Cruise Ship — real launch
                semantics still require every requirement. */}
            {!cruise ? (
              <div className="c-launches">{launchesLineFor(project.id, peopleNeeded)}</div>
            ) : null}
            <div className="c-progress-track">
              <div
                className="c-progress-fill"
                style={{
                  width: `${
                    cruise && cruiseHead && cruiseHead.needed
                      ? Math.min(100, (cruiseHead.filled / cruiseHead.needed) * 100)
                      : Math.min(100, projectProgress(project, pledges) * 100)
                  }%`,
                  background: accent,
                }}
              />
            </div>
            {/* TODO(cruise-demo): Replace the generic "Looking for A
                and B" (which picks the two biggest raw gaps and
                collapses everything else) with a deadpan enumeration
                of the five supporting requirements. Traveler is
                already represented by the headline above. */}
            {cruise && cruiseStillNeed && cruiseStillNeed.length ? (
              <div className="c-cruise-still" data-testid={`cruise-still-need-${project.id}`}>
                <div className="c-cruise-still-label">Still need:</div>
                <div className="c-cruise-still-list">
                  {cruiseStillNeed.map((phrase, i) => (
                    <span key={i} className="c-cruise-still-item">
                      {i > 0 ? <span className="c-cruise-still-sep"> · </span> : null}
                      {phrase}
                    </span>
                  ))}
                </div>
              </div>
            ) : momentum ? (
              <div className="c-momentum" data-testid={`momentum-${project.id}`}>{momentum}</div>
            ) : null}
            {rootsChip}
          </div>
        ) : null}

        {isHappening ? (
          <div className="c-happening-banner">
            <span style={{ fontSize: 18 }}>🌿</span>
            <div className="c-happening-text">It's happening. {helpers} of us brought this to life.</div>
          </div>
        ) : null}

        {isCompleted && project.story ? (
          <div className="c-story-preview">
            <div className="c-story-eyebrow">The story</div>
            <div className="c-story-text">{project.story.what}</div>
            {project.story.photos?.length ? (
              <div className="c-photo-row">
                {project.story.photos.map((p, i) => <span key={i}>{p}</span>)}
              </div>
            ) : null}
          </div>
        ) : null}

        {showRoots && !isGathering ? rootsChip : null}

        {isClosed ? (
          <div className="c-helper">
            {project.nextAttemptId
              ? "This one didn't gather in time — but there's an Attempt 2. Tap in."
              : "This one didn't gather in time. The steward may try again."}
          </div>
        ) : null}
      </div>
    </button>
  );
}
